import { NetworkStatus, gql, useQuery } from '@apollo/client';
import { VStack } from '@chakra-ui/react';
import * as React from 'react';

import { Post } from '@/components/Post';
import { Sentinel } from '@/components/Sentinel';
import { PostPlaceholderFeed } from '@/components/placeholder-feed';

import {
  postFeedV2,
  postFeedV2Variables,
  postFeedV2_postFeedV2_posts,
} from './graphql-types/postFeedV2';

import { NoPosts } from './components/NoPosts';
import { RecentlyAddedSponsors } from './components/RecentlyAddedSponsors';

const postFeedAST = gql`
  query postFeedV2($input: PostFeedInputV2!) {
    postFeedV2(input: $input) {
      posts {
        _id
        currentUserIsPostAuthor
        ...Post_PostV2Fragment
      }
      cursor
      hasMore
    }
  }
  ${Post.postFragment}
`;

export type IPostFeedProps = {
  groupId?: string;
  organizationId?: string;
  tag?: { _id: string; name: string };
  excludePinned?: boolean;
  emptyContent?: React.ReactNode;
  showRecentlyAddedSponsors?: boolean;
};

export const PostFeed: React.FC<IPostFeedProps> = (props) => {
  const {
    groupId,
    tag,
    organizationId,
    showRecentlyAddedSponsors,
    excludePinned,
  } = props;

  const {
    error: feedError,
    data: feedResults,
    fetchMore,
    networkStatus,
  } = useQuery<postFeedV2, postFeedV2Variables>(postFeedAST, {
    variables: {
      input: {
        groupId,
        organizationId,
        tagId: tag?._id,
        excludePinned,
      },
    },
    nextFetchPolicy: 'cache-first',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const hasMore = !!feedResults?.postFeedV2.hasMore;
  const posts = feedResults?.postFeedV2.posts;
  const cursor = feedResults?.postFeedV2.cursor;
  const loadingMore = networkStatus === NetworkStatus.fetchMore;

  const defaultVariables = React.useMemo(
    () => ({
      variables: {
        input: {
          cursor,
          tagId: tag?._id,
          groupId,
          organizationId,
          excludePinned,
        },
      },
    }),
    [cursor, tag, groupId, organizationId, excludePinned]
  );

  const onLoadMore = React.useCallback(
    () => hasMore && fetchMore(defaultVariables),
    [defaultVariables, fetchMore, hasMore]
  );

  if (networkStatus === NetworkStatus.loading) {
    return <PostPlaceholderFeed />;
  }

  if (feedError) {
    throw feedError;
  }

  if (!feedResults || !feedResults.postFeedV2) {
    return null;
  }

  return (
    <React.Fragment>
      {posts && posts.length > 0 ? (
        <>
          <VStack spacing="4">
            {posts.map((post: postFeedV2_postFeedV2_posts, i) => {
              const isSecondOrLastPost =
                i === 1 || (posts.length < 2 && i === posts.length - 1);
              return (
                <React.Fragment key={post._id}>
                  <Post post={post} />
                  {showRecentlyAddedSponsors && isSecondOrLastPost && (
                    <RecentlyAddedSponsors w="full" />
                  )}
                </React.Fragment>
              );
            })}
          </VStack>

          {loadingMore ? (
            <PostPlaceholderFeed w="full" mt="4" />
          ) : (
            <Sentinel onIntersection={onLoadMore} rootMargin="500px 0px" />
          )}
        </>
      ) : props.emptyContent ? (
        props.emptyContent
      ) : showRecentlyAddedSponsors ? (
        <>
          <RecentlyAddedSponsors w="full" my="4" />
          <NoPosts
            tags={tag ? [tag] : undefined}
            organization={organizationId ? { _id: organizationId } : undefined}
            group={groupId ? { _id: groupId } : undefined}
          />
        </>
      ) : (
        <NoPosts
          tags={tag ? [tag] : undefined}
          organization={organizationId ? { _id: organizationId } : undefined}
          group={groupId ? { _id: groupId } : undefined}
        />
      )}
    </React.Fragment>
  );
};
